import { Button } from 'blaze-ui/Button'
import { GenerateButtonProps } from 'components/Wizards/Field/Types'
import { useCreateFieldsetCompletion } from 'hooks/queries/workspaces/useCreateFieldsetCompletion'
import { forwardRef, useCallback, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useAutoCompleteFields } from 'components/Wizards/Wizard/Form/useAutoCompleteFields'
import { FormStatus } from 'components/Wizards/Wizard/Form/Types'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'
import { AIJobStatus, FieldsetCompletion } from 'types'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useWizardContext } from 'components/Wizards/Wizard/WizardContext'

export const GenerateButton = forwardRef<HTMLButtonElement, GenerateButtonProps>(
  (
    {
      field: fieldName,
      onPress,
      onSettled,
      label = 'Fill in this box for me',
      size = 'xs',
      frontIcon,
      variant = 'secondary',
      isDisabled,
    },
    fRef,
  ) => {
    const { schema } = useWizardContext()
    const { setStatus, initialStatus, values } = useFormikContext<{ [key: string]: any }>()

    const generationType = useMemo(() => {
      if ('describe' in schema) {
        return schema.describe().type
      }
      return schema.meta.channel
    }, [schema])

    const completedFields = useMemo(() => {
      const valueEntries = Object.entries(values)
      const nonEmptyValues = valueEntries.filter(([_key, value]) => {
        if (Array.isArray(value)) {
          return value.length !== 0
        }
        return value !== '' && value !== null
      })
      return Object.fromEntries(nonEmptyValues)
    }, [values])

    const { mutate, data } = useCreateFieldsetCompletion()
    const autoCompleteFields = useAutoCompleteFields()

    const handleComplete = useCallback(
      async (fieldsetCompletion: FieldsetCompletion) => {
        if (fieldsetCompletion.status === AIJobStatus.Failed) {
          showErrorMessageToast('Blaze was unable to generate a topic. Please contact support if this issue persists.')
          setStatus(initialStatus)
          onSettled()
          return
        }

        try {
          await autoCompleteFields(fieldsetCompletion.completed_fields)
          setStatus(FormStatus.AutoCompleted)
        } catch {
          setStatus(initialStatus)
        } finally {
          onSettled()
        }
      },
      [autoCompleteFields], // eslint-disable-line react-hooks/exhaustive-deps
    )

    const handlePress = () => {
      onPress()
      setStatus(initialStatus)
      mutate(
        { fields: [fieldName], completedFields, generationType },
        {
          onError: () => {
            setStatus(initialStatus)
            onSettled()
          },
        },
      )
    }

    return (
      <PollFieldsetCompletion fieldsetCompletionId={data?.fieldset_completion.id} onComplete={handleComplete}>
        {({ fieldsetCompletion }) => {
          const isGenerationDisabled =
            isDisabled ||
            (fieldsetCompletion && [AIJobStatus.Pending, AIJobStatus.Started].includes(fieldsetCompletion.status))
          return (
            <Button
              ref={fRef}
              size={size}
              frontIcon={frontIcon}
              isDisabled={isGenerationDisabled}
              onPress={handlePress}
              variant={variant}
            >
              {label}
            </Button>
          )
        }}
      </PollFieldsetCompletion>
    )
  },
)
